import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDispatch, useSelector } from "react-redux";
import { Download as DownloadIcon } from "@mui/icons-material";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  useTheme,
  Button,
  IconButton,
  TablePagination,
  Grid,
  Autocomplete,
} from "@mui/material";

import {
  Search as SearchIcon,
  AccessTime as AccessTimeIcon,
  Logout as LogoutIcon,
  Pending as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarTodayIcon,
  Groups as GroupsIcon,
} from "@mui/icons-material";

import attendanceSvg from "assets/folders/attendance.svg";

import {
  fetchTeamAttendanceRecords,
  verifyAttendance,
} from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsActions";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";
import { selectCurrentUser } from "store/features/auth/authSelectors";

import {
  selectAttendanceRecords,
  selectAttendanceRecordsLoading,
  selectAttendanceRecordsPagination,
} from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsSelectors";

import usePermissions from "hooks/usePermissions";

const TeamAttendance = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const user = useSelector(selectCurrentUser);
  const userRole = user?.role;
  const attendanceRecords = useSelector(selectAttendanceRecords);
  const loading = useSelector(selectAttendanceRecordsLoading);
  const pagination = useSelector(selectAttendanceRecordsPagination);
  const employees = useSelector(selectEmployees);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const { hasPermission } = usePermissions();
  const canApprove =
    hasPermission("attendance.approve") ||
    hasPermission("attandance.approve") ||
    userRole === "Company";

  const handleVerify = async (id, approval_status) => {
    setActionLoadingId(id);
    const currentParams = {};
    if (statusFilter !== "all") currentParams.status = statusFilter;
    if (fromDate) currentParams.from_date = fromDate;
    if (toDate) currentParams.to_date = toDate;
    if (selectedEmployee) currentParams.employee_id = selectedEmployee.id;
    if (searchTerm) currentParams.search = searchTerm;
    currentParams.page = page + 1;
    currentParams.limit = rowsPerPage;

    await dispatch(verifyAttendance(id, { approval_status }, currentParams));
    setActionLoadingId(null);
  };

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  useEffect(() => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (selectedEmployee) params.employee_id = selectedEmployee.id;
    if (searchTerm) params.search = searchTerm;
    params.page = page + 1;
    params.limit = rowsPerPage;

    dispatch(fetchTeamAttendanceRecords(params));
  }, [statusFilter, fromDate, toDate, selectedEmployee, searchTerm, page, rowsPerPage, dispatch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedEmployee(null);
    setStatusFilter("all");
    setFromDate("");
    setToDate("");
    setPage(0);
  };

  const filteredRecords = useMemo(() => {
    let filtered = attendanceRecords || [];

    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((record) => {
        const empName = `${record.first_name || ""} ${record.last_name || ""}`.toLowerCase();
        const empCode = (record.employee_code || "").toLowerCase();
        const email = (record.email || "").toLowerCase();
        const phone = (record.phone || "").toLowerCase();
        const remarks = (record.remarks || "").toLowerCase();

        return (
          empName.includes(term) ||
          empCode.includes(term) ||
          email.includes(term) ||
          phone.includes(term) ||
          remarks.includes(term)
        );
      });
    }

    if (selectedEmployee) {
      filtered = filtered.filter(
        (record) => record.employee_id === selectedEmployee.id
      );
    }

    return filtered;
  }, [attendanceRecords, searchTerm, selectedEmployee]);

  const statistics = useMemo(() => {
    const total = attendanceRecords?.length || 0;
    const present = attendanceRecords?.filter((r) => r.status === "present" || r.status === "late" || r.check_in !== null).length || 0;
    const absent = attendanceRecords?.filter((r) => r.status === "absent" || r.check_in === null).length || 0;
    const late = attendanceRecords?.filter((r) => r.is_late === 1 || r.status === "late").length || 0;

    return { total, present, absent, late };
  }, [attendanceRecords]);

  // const exportToExcel = () => {
  //   const exportData = filteredRecords.map((r, index) => {
  //     const formattedDate = r.created_at
  //       ? dayjs(r.created_at).format("YYYY-MM-DD")
  //       : r.record_date || "-";
  //     const checkInTime = r.check_in
  //       ? dayjs(r.check_in).format("hh:mm A")
  //       : "-";
  //     const checkOutTime = r.check_out
  //       ? dayjs(r.check_out).format("hh:mm A")
  //       : "-";

  //     let workHrsStr = "-";
  //     if (r.working_minutes) {
  //       const hrs = Math.floor(r.working_minutes / 60);
  //       const mins = r.working_minutes % 60;
  //       workHrsStr = `${hrs}h ${mins}m`;
  //     }

  //     return {
  //       "S.No": index + 1,
  //       "Employee Code": r.employee_code || "-",
  //       "Employee Name": `${r.first_name || ""} ${r.last_name || ""}`.trim() || "-",
  //       Date: formattedDate,
  //       "Check In": checkInTime,
  //       "Check Out": checkOutTime,
  //       "Work Hours": workHrsStr,
  //       "Late Status": r.is_late === 1 ? `Late (${r.late_minutes || 0}m)` : "On Time",
  //       "Attendance Status": (r.status || "Absent").toUpperCase(),
  //       "Approval Status": (r.approval_status || "-").toUpperCase(),
  //       Remarks: r.remarks || "-",
  //     };
  //   });

  //   const worksheet = XLSX.utils.json_to_sheet(exportData);
  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "Team Attendance");

  //   const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  //   const blob = new Blob([excelBuffer], {
  //     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  //   });
  //   saveAs(blob, `Team_Attendance_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  // };

  const getStatusChip = (status, checkIn, row = {}) => {
    const rawStatus = (row.status || status || "").toLowerCase();

    if (rawStatus === "holiday_present" || (row.is_holiday && checkIn)) {
      return (
        <Chip
          label="Holiday Present [HP]"
          size="small"
          sx={{
            bgcolor: "success.soft",
            color: "success.main",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      );
    }
    if (rawStatus === "week_off_present" || (row.is_week_off && checkIn)) {
      return (
        <Chip
          label="Week Off Present [WoP]"
          size="small"
          sx={{
            bgcolor: "success.soft",
            color: "success.main",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      );
    }
    if (rawStatus === "leave_present" || (row.is_leave && checkIn)) {
      return (
        <Chip
          label="Leave Present [LP]"
          size="small"
          sx={{
            bgcolor: "success.soft",
            color: "success.main",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      );
    }
    if (rawStatus === "late" || row.is_late) {
      return (
        <Chip
          label="Late [L]"
          size="small"
          sx={{
            bgcolor: "warning.soft",
            color: "warning.main",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      );
    }
    if (rawStatus === "present" || checkIn !== null) {
      return (
        <Chip
          label="Present [P]"
          size="small"
          sx={{
            bgcolor: "success.soft",
            color: "success.main",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      );
    }
    if (rawStatus === "holiday" || row.is_holiday) {
      return (
        <Chip
          label={row.holiday_name ? `Holiday [H]: ${row.holiday_name}` : "Holiday [H]"}
          size="small"
          sx={{
            bgcolor: "info.soft",
            color: "info.main",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      );
    }
    if (rawStatus === "on_leave" || rawStatus === "leave" || row.is_leave) {
      return (
        <Chip
          label={row.leave_name ? `On Leave: ${row.leave_name}` : "On Leave"}
          size="small"
          sx={{
            bgcolor: "secondary.soft",
            color: "secondary.main",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      );
    }
    if (rawStatus === "week_off" || row.is_week_off) {
      return (
        <Chip
          label="Week Off [WO]"
          size="small"
          sx={{
            bgcolor: "action.selected",
            color: "text.secondary",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      );
    }
    return (
      <Chip
        label="Absent [A]"
        size="small"
        sx={{
          bgcolor: "error.soft",
          color: "error.main",
          fontWeight: 600,
          fontSize: "0.75rem",
        }}
      />
    );
  };

  const getApprovalChip = (approvalStatus) => {
    if (!approvalStatus) return <Typography variant="caption" color="text.secondary">-</Typography>;
    switch (approvalStatus.toLowerCase()) {
      case "approved":
        return (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
            label="Approved"
            size="small"
            color="success"
            variant="outlined"
          />
        );
      case "rejected":
        return (
          <Chip
            icon={<CancelIcon sx={{ fontSize: "14px !important" }} />}
            label="Rejected"
            size="small"
            color="error"
            variant="outlined"
          />
        );
      case "pending":
      default:
        return (
          <Chip
            icon={<PendingIcon sx={{ fontSize: "14px !important" }} />}
            label="Pending"
            size="small"
            color="warning"
            variant="outlined"
          />
        );
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: 1,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(15,23,42,0.9) 100%)"
              : "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
          border: `1px solid ${theme.palette.divider}`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1,
                bgcolor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                boxShadow: "0 8px 16px -4px rgba(79, 70, 229, 0.4)",
              }}
            >
              <GroupsIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                Team Attendance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track and monitor real-time daily attendance of your reporting team members
              </Typography>
            </Box>
          </Stack>

          {/* <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            sx={{
              borderRadius: 1,
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(79, 70, 229, 0.25)",
            }}
          >
            Export Team Report
          </Button> */}
        </Stack>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              TOTAL TEAM RECORDS
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
              {statistics.total}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Typography variant="caption" color="success.main" fontWeight={600}>
              PRESENT
            </Typography>
            <Typography variant="h4" fontWeight={700} color="success.main" sx={{ mt: 1 }}>
              {statistics.present}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Typography variant="caption" color="error.main" fontWeight={600}>
              ABSENT
            </Typography>
            <Typography variant="h4" fontWeight={700} color="error.main" sx={{ mt: 1 }}>
              {statistics.absent}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Typography variant="caption" color="warning.main" fontWeight={600}>
              LATE ARRIVALS
            </Typography>
            <Typography variant="h4" fontWeight={700} color="warning.main" sx={{ mt: 1 }}>
              {statistics.late}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search team member..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid> */}

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              sx={{
                minWidth: '300px'
              }}
              options={employees || []}
              getOptionLabel={(option) =>
                `${option.first_name || ""} ${option.last_name || ""} (${option.employee_code || ""})`.trim()
              }
              value={selectedEmployee}
              onChange={(event, newValue) => setSelectedEmployee(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Filter Team Member" size="small" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="From Date"
              slotProps={{ textField: { size: "small", fullWidth: true } }}
              value={fromDate ? dayjs(fromDate) : null}
              onChange={(newValue) =>
                setFromDate(newValue ? newValue.format("YYYY-MM-DD") : "")
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="To Date"
              slotProps={{ textField: { size: "small", fullWidth: true } }}
              value={toDate ? dayjs(toDate) : null}
              onChange={(newValue) =>
                setToDate(newValue ? newValue.format("YYYY-MM-DD") : "")
              }
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              size="small"
              sx={{ py: 0.9, borderRadius: 1 }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 700 }}>Team Member</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Check In</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Check Out</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Working Hours</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Punctuality</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                {canApprove && <TableCell sx={{ fontWeight: 700 }}>Approval</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canApprove ? 8 : 7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Loading team attendance records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canApprove ? 8 : 7} align="center" sx={{ py: 6 }}>
                    <Box
                      component="img"
                      src={attendanceSvg}
                      alt="No records"
                      sx={{ width: 120, height: 120, mb: 2, opacity: 0.7 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No Team Attendance Records Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      There are no attendance records matching your criteria for team reportees.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRecords.map((row) => {
                  const checkInFormatted = row.check_in
                    ? dayjs(row.check_in).format("hh:mm A")
                    : "-";
                  const checkOutFormatted = row.check_out
                    ? dayjs(row.check_out).format("hh:mm A")
                    : "-";
                  const dateFormatted = row.created_at
                    ? dayjs(row.created_at).format("DD MMM YYYY")
                    : row.record_date
                      ? dayjs(row.record_date).format("DD MMM YYYY")
                      : "-";

                  let workHrsStr = "-";
                  if (row.working_minutes) {
                    const hrs = Math.floor(row.working_minutes / 60);
                    const mins = row.working_minutes % 60;
                    workHrsStr = `${hrs}h ${mins}m`;
                  }

                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              bgcolor: "primary.soft",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 700,
                              fontSize: "0.9rem",
                            }}
                          >
                            {row.first_name ? row.first_name.charAt(0).toUpperCase() : "E"}
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {`${row.first_name || ""} ${row.last_name || ""}`.trim() || "Employee"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Code: {row.employee_code || "-"}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.8} alignItems="center">
                          <CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                          <Typography variant="body2">{dateFormatted}</Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.8} alignItems="center">
                          <AccessTimeIcon sx={{ fontSize: 16, color: row.check_in ? "success.main" : "action.disabled" }} />
                          <Typography variant="body2" fontWeight={row.check_in ? 600 : 400}>
                            {checkInFormatted}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.8} alignItems="center">
                          <LogoutIcon sx={{ fontSize: 16, color: row.check_out ? "info.main" : "action.disabled" }} />
                          <Typography variant="body2">{checkOutFormatted}</Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">{workHrsStr}</Typography>
                      </TableCell>

                      <TableCell>
                        {row.is_late === 1 ? (
                          <Chip
                            label={`Late (${row.late_minutes || 0}m)`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            On Time
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {getStatusChip(row.status, row.check_in, row)}
                      </TableCell>

                      {canApprove && (
                        <TableCell>
                          {row.id && typeof row.id === "number" ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              {getApprovalChip(row.approval_status)}
                              {row.approval_status === "pending" && (
                                <Stack direction="row" spacing={0.5}>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleVerify(row.id, "approved")}
                                    disabled={actionLoadingId === row.id}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleVerify(row.id, "rejected")}
                                    disabled={actionLoadingId === row.id}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination?.total_records || filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default TeamAttendance;
